import { filterTree } from "@/Common/helpers/Arrays/tree";
import SearchInput from "@/Layouts/components/Basics/SearchInput";
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import AppSideBar, { SideBarContent } from "@/Layouts/components/SideBars/AppSideBar";
import SideTreeView from "@/Layouts/components/SideBars/SideTreeView";
import { IGetSupportRequestsTreeResult, TreeNode } from "@/Washouts/api/SupportRequests/queries";
import { useChangeRequestSubscriptions } from "@/Washouts/hooks/ChangeRequests/useChangeRequestSubscriptions";
import { useCommissionSupportTeam } from "@/Washouts/hooks/useCommissionSupportTeam";
import useAccessPositions from "@/Washouts/hooks/WashoutReview/useAccessPositions";
import { Box, Divider, Stack } from "@mui/material";
import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";

const propagateIds = (tree: TreeNode[], ids: Partial<Record<SupportTreeLevels, string>> = {}): ExtendedTreeNode[] =>
	tree.map((node) => {
		const newIds = { ...ids, [node.levelName ?? ""]: node._id };
		const list = node.list ? propagateIds(node.list, newIds) : undefined;
		return { ...node, ids: newIds, list };
	});

type TreeProperties = "showReviewTree" | "reviewTree" | "currentMyTree" | "previousMyTree";

export interface ISupportRequestsSidebarProps extends Omit<ISupportRequestsAppSidebarSelector, TreeProperties> {
	isLoading: boolean;
	tree?: IGetSupportRequestsTreeResult;
}

export const SupportRequestsSidebar = (props: Readonly<ISupportRequestsSidebarProps>) => {
	const { isLoading, tree, ...appSidebarProps } = props;

	const [reviewer, setReviewer] = useState(false);
	const [disableReviewSwitch, setDisableReviewSwitch] = useState(true);

	const loadedRef = useRef(false);

	const positionAccess = useAccessPositions();

	const { userIsInSupportTeam, isLoading: isLoadingSupportTeam } = useCommissionSupportTeam();

	const isLoadingAnything = isLoading || isLoadingSupportTeam;

	const hasReviewAccess = Object.keys(positionAccess).some((key) => positionAccess[key].edit) || !!userIsInSupportTeam;

	useChangeRequestSubscriptions({
		enabled: hasReviewAccess && !isLoadingAnything,
	});

	useEffect(() => {
		if (!isLoading && !isLoadingSupportTeam && !loadedRef.current) {
			setReviewer(hasReviewAccess);
			loadedRef.current = true;
		}
		if (!isLoading && !isLoadingSupportTeam && props.tree) {
			const hasMyTree = Boolean(props.tree?.myTree.currentCommissionCycle?.length);
			setDisableReviewSwitch(!hasReviewAccess || !hasMyTree);
		}
	}, [isLoading, props.tree, hasReviewAccess, isLoadingSupportTeam]);

	const { reviewTree, previousMyTree, currentMyTree } = useMemo(() => {
		if (!tree) {
			return {};
		}
		return {
			reviewTree: propagateIds(tree.positionsTree),
			currentMyTree: propagateIds(tree.myTree.currentCommissionCycle),
			previousMyTree: propagateIds([
				{
					_id: "rootNode",
					title: "Previous Support",
					levelName: "rootNode",
					badge: {},
					bold: true,
					disabled: !tree.myTree.previousSupport.length,
					status: "",
					readonly: false,
					list: tree.myTree.previousSupport,
				},
			]),
		};
	}, [tree]);

	if (isLoadingAnything) {
		return (
			<AppSideBar
				searchProps={{
					loading: true,
				}}
				appendProps={{
					loading: true,
					loadingRows: 2,
					divider: true,
				}}
				sideBarProps={{
					loading: true,
					loadingRows: 4,
				}}
			/>
		);
	}

	if (!isLoadingAnything && !tree) {
		return null;
	}

	return (
		<AppSideBar
			sideBarSection={
				<Stack className="justify-between h-[620px] pb-6" direction="column">
					<Box className="">
						<SupportRequestsAppSidebarSelector
							showReviewTree={reviewer}
							{...appSidebarProps}
							reviewTree={reviewTree}
							previousMyTree={previousMyTree}
							currentMyTree={currentMyTree}
						/>
					</Box>
					<Box justifyContent="center" className="flex [&_label]:!mr-0 mt-2">
						<CustomizedSwitches
							disabled={disableReviewSwitch}
							checked={reviewer}
							onChange={(value) => {
								appSidebarProps.handleSelection(null, value ? "ReviewTree" : "CurrentMyTree");
								setReviewer(value);
							}}
							textChecked={reviewer ? "Reviewer" : "Requester"}
							textUnChecked={reviewer ? "Reviewer" : "Requester"}
							textRight
						/>
					</Box>
				</Stack>
			}
		/>
	);
};

const getSelectedId = (selected: TreeNode | null, currentTree: string, treeName: string) => {
	if (currentTree === treeName && selected) {
		return selected._id;
	}
	return "";
};

type TreeName = "ReviewTree" | "CurrentMyTree" | "PreviousMyTree";

type SupportTreeLevels = "year" | "commissionCycle" | "payPeriod" | "position" | "user";

export interface ExtendedTreeNode extends TreeNode {
	ids?: Partial<Record<SupportTreeLevels, string>>;
}

export interface ISupportRequestsAppSidebarSelector {
	reviewTree?: ExtendedTreeNode[];
	currentMyTree?: ExtendedTreeNode[];
	previousMyTree?: ExtendedTreeNode[];
	selected: ExtendedTreeNode | null;
	currentTree: TreeName;
	handleSelection: (node: ExtendedTreeNode | null, tree: TreeName) => void;
	showReviewTree: boolean;
	extraContent?: React.ReactNode;
}

export const SupportRequestsAppSidebarSelector = (props: Readonly<ISupportRequestsAppSidebarSelector>) => {
	const { reviewTree, currentMyTree, previousMyTree, selected, handleSelection, showReviewTree, currentTree } = props;

	const [search, setSearch] = useState("");

	const filteredAppendTree = filterTree(currentMyTree ?? [], {
		text: search,
		index: "title",
		dep: "list",
	});

	const filteredSideBarTree = filterTree(previousMyTree ?? [], {
		text: search,
		index: "title",
		dep: "list",
	});

	return (
		<>
			<Box
				className={clsx('[&_ul[role="tree"]]:!max-h-[515px]', {
					hidden: !showReviewTree,
				})}
			>
				<SideBarContent
					searchProps={{
						loading: false,
					}}
					sideBarProps={{
						tree: reviewTree ?? [],
						selected: getSelectedId(selected, currentTree, "ReviewTree"),
						onSelectAction: (node: ExtendedTreeNode) => handleSelection(node, "ReviewTree"),
					}}
				/>
			</Box>
			<Box
				className={clsx({
					hidden: showReviewTree,
				})}
			>
				<Box className="px-[15px]">
					<SearchInput value={search} clearSearch={() => setSearch("")} onChange={(val) => setSearch(val)} />
				</Box>
				<Stack className="space-y-4 mt-[14.4px]">
					<Box className='[&_ul[role="tree"]]:!max-h-[150px]'>
						<SideTreeView
							tree={filteredAppendTree}
							label={{
								title: "Current Commission Cycle(s)",
								emptyTitle: "No active Commission Cycles to show",
								isEmpty: () => !currentMyTree?.length,
							}}
							selected={getSelectedId(selected, currentTree, "CurrentMyTree")}
							onSelectAction={(node) => handleSelection(node as ExtendedTreeNode, "CurrentMyTree")}
						/>
					</Box>
					<Divider variant="middle" className="!border-disabled" />
					<Box className='[&_ul[role="tree"]]:!max-h-[330px]'>
						<SideTreeView
							tree={filteredSideBarTree}
							selected={getSelectedId(selected, currentTree, "PreviousMyTree")}
							onSelectAction={(node) => handleSelection(node as ExtendedTreeNode, "PreviousMyTree")}
						/>
					</Box>
				</Stack>
			</Box>
		</>
	);
};
